import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import helpDocs from './helpDocs';

// ── Content block renderer ────────────────────────────────────────────────────
function ContentBlock({ item }) {
  if (item.type === 'p') {
    return <p className="text-[13.5px] text-gray-700 leading-relaxed mb-3">{item.text}</p>;
  }
  if (item.type === 'step') {
    return (
      <div className="flex gap-3 mb-2.5">
        <span className="shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5 select-none">→</span>
        <p className="text-[13.5px] text-gray-700 leading-relaxed m-0">{item.text}</p>
      </div>
    );
  }
  if (item.type === 'tip') {
    return (
      <div className="flex gap-2.5 bg-blue-50 border border-[color:light-dark(#bfdbfe,#113462)] rounded-lg px-3.5 py-3 mb-3">
        <span className="text-[15px] shrink-0 mt-0.5">💡</span>
        <p className="text-[13px] text-[color:light-dark(#1e40af,#8099e9)] leading-relaxed m-0 font-medium">{item.text}</p>
      </div>
    );
  }
  if (item.type === 'warning') {
    return (
      <div className="flex gap-2.5 bg-[color:light-dark(#fff7ed,#3b2d1b)] border border-[color:light-dark(#fed7aa,#5b3b17)] rounded-lg px-3.5 py-3 mb-3">
        <span className="text-[15px] shrink-0 mt-0.5">⚠️</span>
        <p className="text-[13px] text-[color:light-dark(#9a3412,#efa78e)] leading-relaxed m-0 font-medium">{item.text}</p>
      </div>
    );
  }
  if (item.type === 'list') {
    return (
      <div className="flex gap-2.5 mb-1.5 items-start">
        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-[7px]"></span>
        <p className="text-[13.5px] text-gray-700 leading-relaxed m-0">{item.text}</p>
      </div>
    );
  }
  return null;
}

// ── Table of Contents ─────────────────────────────────────────────────────────
function TableOfContents({ doc, onSectionClick }) {
  return (
    <div className="px-5 pt-3 pb-2 border-b border-slate-100 shrink-0 bg-[color:light-dark(#fafbfc,#20242b)]">
      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">On this page</p>
      <div className="flex flex-wrap gap-1.5">
        {doc.sections.map((section, idx) => (
          <button
            key={idx}
            onClick={() => onSectionClick(idx)}
            className="text-xs text-indigo-500 bg-[color:light-dark(#eef2ff,#121f44)] hover:bg-[color:light-dark(#e0e7ff,#121f44)] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap"
          >
            {section.heading}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Article view ──────────────────────────────────────────────────────────────
function ArticleView({ doc }) {
  const sectionRefs = useRef([]);
  const scrollRef = useRef(null);

  const scrollToSection = (idx) => {
    const el = sectionRefs.current[idx];
    if (el && scrollRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex flex-col overflow-hidden h-full">
      <TableOfContents doc={doc} onSectionClick={scrollToSection} />
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
        {/* Module Header */}
        <div className="flex items-center gap-3 mb-6 pb-5 border-b border-slate-100">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${doc.color}`}>
            {doc.icon}
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-slate-900 m-0 leading-tight">{doc.title}</h2>
            <p className="text-[12px] text-slate-400 m-0 mt-0.5">{doc.sections.length} topics</p>
          </div>
        </div>

        {/* Sections */}
        {doc.sections.map((section, idx) => (
          <div
            key={idx}
            ref={el => sectionRefs.current[idx] = el}
            className="mb-7 scroll-mt-4"
          >
            <h3 className="text-[14.5px] font-bold text-slate-900 mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-indigo-500 rounded-full shrink-0"></span>
              {section.heading}
            </h3>
            <div className="pl-3">
              {section.content.map((item, cIdx) => (
                <ContentBlock key={cIdx} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Search results view ───────────────────────────────────────────────────────
function SearchResults({ query, onSelect }) {
  const results = [];
  helpDocs.forEach(doc => {
    doc.sections.forEach(section => {
      const haystack = (doc.title + ' ' + section.heading + ' ' + section.content.map(c => c.text).join(' ')).toLowerCase();
      if (haystack.includes(query.toLowerCase())) {
        results.push({ doc, section });
      }
    });
  });

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-400 px-6">
        <span className="text-4xl mb-3">🔍</span>
        <p className="text-[14px] font-medium m-0 text-center">No results for "{query}"</p>
        <p className="text-[12px] m-0 mt-1">Try a different keyword</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 overflow-y-auto h-full">
      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3 px-2">
        {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
      </p>
      {results.map((r, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(r.doc)}
          className="w-full text-left px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors mb-1.5 group border border-transparent hover:border-slate-200"
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs ${r.doc.color}`}>{r.doc.icon}</span>
            <span className="text-xs text-indigo-500 font-semibold">{r.doc.title}</span>
          </div>
          <p className="text-[13px] font-semibold text-slate-900 m-0 mb-1 group-hover:text-blue-600 transition-colors">{r.section.heading}</p>
          <p className="text-[12px] text-slate-500 m-0 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {r.section.content.find(c => c.type === 'p' || c.type === 'step')?.text || ''}
          </p>
        </button>
      ))}
    </div>
  );
}

// ── Home view ─────────────────────────────────────────────────────────────────
function HomeView({ onSelect }) {
  return (
    <div className="px-5 py-5 overflow-y-auto h-full">
      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-4">All Modules</p>
      <div className="grid grid-cols-2 gap-2.5">
        {helpDocs.map(doc => (
          <button
            key={doc.id}
            onClick={() => onSelect(doc)}
            className="flex flex-col items-start gap-2 p-3.5 rounded-xl border border-slate-100 hover:border-[color:light-dark(#e0e7ff,#0f2463)] hover:bg-[color:light-dark(#f8faff,#20242b)] transition-all text-left group"
          >
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${doc.color}`}>{doc.icon}</span>
            <span className="text-[13px] font-semibold text-slate-800 group-hover:text-blue-600 transition-colors leading-tight">{doc.title}</span>
            <span className="text-xs text-slate-400">{doc.sections.length} topics</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main HelpCenter component ─────────────────────────────────────────────────
export default function HelpCenter({ isOpen, onClose, currentRoute }) {
  const [activeDoc, setActiveDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  // Context-aware: auto-open to matching module doc based on current route
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      if (currentRoute) {
        const matched = helpDocs.find(d => d.route && currentRoute.startsWith(d.route));
        setActiveDoc(matched || null);
      } else {
        setActiveDoc(null);
      }
      setTimeout(() => searchRef.current?.focus(), 150);
    }
  }, [isOpen, currentRoute]);

  // ESC key to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleSelect = useCallback((doc) => {
    setActiveDoc(doc);
    setSearchQuery('');
  }, []);

  const handleBack = useCallback(() => {
    setActiveDoc(null);
    setSearchQuery('');
  }, []);

  if (!isOpen) return null;

  const showSearch = searchQuery.trim().length >= 2;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/20-[2px] z-[var(--cx-z-modal)] cursor-pointer"
      />

      {/* Slide-over Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Help Center"
        className="fixed top-0 right-0 bottom-0 z-[var(--cx-z-modal)] flex flex-col bg-white shadow-2xl"
        style={{
          width: '460px',
          borderLeft: '1px solid var(--cx-line)',
          animation: 'helpSlideIn 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          {activeDoc && !showSearch ? (
            <button
              onClick={handleBack}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors shrink-0"
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : (
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${activeDoc ? activeDoc.color : 'bg-[#6366f1]/10'}`}>
              <span className="text-sm">{activeDoc ? activeDoc.icon : '❓'}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-slate-900 m-0 leading-tight">
              {activeDoc && !showSearch ? activeDoc.title : 'Help Center'}
            </h2>
            <p className="text-xs text-slate-400 m-0">
              {activeDoc && !showSearch ? `${activeDoc.sections.length} topics` : `${helpDocs.length} modules documented`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-gray-700 transition-colors shrink-0"
            aria-label="Close help center"
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Search Bar ── */}
        <div className="px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3.5 h-9 focus-within:border-indigo-500 focus-within:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] transition-all">
            <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search menus, features, topics..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-[13px] text-gray-700 placeholder:text-slate-400 font-medium"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-gray-700 transition-colors">
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Module Pills (home screen only) ── */}
        {!showSearch && !activeDoc && (
          <div className="px-4 py-2 flex gap-2 overflow-x-auto shrink-0 border-b border-slate-100" style={{ scrollbarWidth: 'none' }}>
            {helpDocs.slice(0, 7).map(doc => (
              <button
                key={doc.id}
                onClick={() => handleSelect(doc)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${doc.color}`}
              >
                <span>{doc.icon}</span>
                <span>{doc.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── Main Content Area ── */}
        <div className="flex-1 overflow-hidden">
          {showSearch
            ? <SearchResults query={searchQuery} onSelect={handleSelect} />
            : activeDoc
              ? <ArticleView doc={activeDoc} />
              : <HomeView onSelect={handleSelect} />
          }
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-3 border-t border-slate-100 shrink-0 flex items-center justify-between bg-[color:light-dark(#fafbfc,#20242b)]">
          <p className="text-xs text-slate-400 m-0">
            Press <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs font-mono text-slate-500 shadow-sm">ESC</kbd> to close
          </p>
          {activeDoc && (
            <button onClick={handleBack} className="text-[12px] text-indigo-500 font-semibold hover:underline">
              ← All modules
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes helpSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>,
    document.body
  );
}
